export const en = {
  nav: {
    about: 'About',
    chat: 'AI Chat',
    experience: 'Experience',
    projects: 'Featured Work',
    research: 'Research',
    contact: 'Contact',
  },
  hero: {
    greeting: "Hi, I'm",
    name: 'Stefans',
    title: 'Web3 Researcher · DeFi Protocol Expert · Quant Strategist · AI Builder',
    subtitle:
      'Understanding protocol mechanics from first principles, turning insights into executable strategies, products, and research systems — amplified by AI.',
    rare_combo: 'Protocol Research × DeFi Products × Quant Strategy × AI Systems — a rare combination',
    cta_chat: 'Chat with My AI',
    cta_projects: 'View Featured Work',
    cta_research: 'Read Hyperliquid Research',
    badge_web3: 'Web3 & DeFi',
    badge_quant: 'Quant Strategy',
    badge_ai: 'AI Builder',
  },
  stats: {
    experience: 'Years Tech & Product',
    tvl: 'Peak TVL',
    yield: 'Quant Annual Yield',
    articles: 'Research Articles',
    experience_label: 'From internet products to DeFi full-stack',
    tvl_label: 'KayaLand actively managed DeFi fund',
    yield_label: 'Martingale + LP combined strategy',
    articles_label: 'Covering the full Web3 landscape',
  },
  about: {
    title: 'About',
    subtitle: 'Not just a Web3 user — a protocol researcher and strategy builder',
    p1: '15 years of end-to-end tech-product-quant experience. Started from traditional internet products, went through the full DeFi Summer cycle, deeply involved in the complete evolution from RWA tokenization, DeFi protocol design, investment research, to on-chain quant strategy development.',
    p2: 'My passion for Web3 comes from the shock of reading the Bitcoin whitepaper — Satoshi used an elegantly minimal design to solve a trust problem that plagued humanity for millennia. This first-principles thinking shapes how I understand every protocol and strategy.',
    p3: "When AI meets Web3's composability, it's the direction I find most worth pursuing: one person + AI can accomplish what traditionally required an entire team.",
    tag_web3: 'DeFi Protocol Depth',
    tag_quant: 'Quant Strategy',
    tag_ai: 'AI-Native Workflow',
    tag_research: 'Research Output',
    tag_product: '0-to-1 Products',
    tag_teaching: 'Knowledge Sharing',
    capabilities_title: 'What I Focus On',
    capabilities: [
      {
        icon: '🔬',
        title: 'Protocol Research',
        desc: 'First-principles analysis of protocol design — Hyperliquid HFT infrastructure, DeFi mechanics, Layer2 technical routes',
      },
      {
        icon: '⚙️',
        title: 'DeFi Product & Strategy',
        desc: 'From protocol understanding to product execution — KayaLand $2M+ TVL, UniOcean solo-built, RWA tokenization',
      },
      {
        icon: '📈',
        title: 'Quant System Development',
        desc: 'In-house multi-strategy quant framework — Martingale+LP combined 60%+ annual, regime detection, risk management',
      },
      {
        icon: '🤖',
        title: 'AI Agent & Research Tools',
        desc: 'Systematizing research capability into AI products — digital twin, multi-agent research system, DeFi info agent',
      },
    ],
  },
  chat: {
    title: 'Ask My AI Directly',
    subtitle: 'Research, projects, strategies, experience — all fair game. Built on real background, answers from my perspective.',
    placeholder: 'Ask me anything, e.g.: What did you research about Hyperliquid?',
    send: 'Send',
    new_chat: 'New Chat',
    thinking: 'Thinking...',
    welcome: "Hi! Ask me about my Web3 research, DeFi products, quant strategies, AI systems, or anything you'd like to explore.",
    suggestions: [
      'What did your Hyperliquid research uncover?',
      'How do you turn protocol research into products or strategies?',
      'How did KayaLand reach $2M+ TVL?',
      'What is your multi-agent research system?',
    ],
    limit_chat: 'Chat limit reached. Please start a new conversation.',
    limit_total: 'Daily limit reached (20 messages/day). Resets tomorrow.',
    error: 'An error occurred. Please try again.',
  },
  experience: {
    title: 'Experience',
    subtitle: 'My Web3 journey from 2018',
    pre_web3: 'Pre-2018: 9 years of traditional internet tech & product experience',
    items: [
      {
        year: '2026',
        title: 'Hyperliquid HFT Infrastructure Research',
        company: 'Technical Consulting Project',
        desc: 'Commissioned by a cloud provider to comprehensively research Hyperliquid HFT market-making infrastructure — deploying Non-Validating nodes and studying HyperBFT consensus mechanics and latency.',
        bullets: [
          'Delivered complete HFT practice guide for institutional market-making teams',
          'Quantified node jitter, latency distribution, and market-maker priority mechanisms',
        ],
        tags: ['Hyperliquid', 'HFT', 'Node Deploy', 'Latency Research'],
      },
      {
        year: '2024',
        title: 'Quant Strategy Researcher & AI Product Developer',
        company: 'Independent',
        desc: 'Built in-house multi-strategy quant framework while systematizing research capability into AI products.',
        bullets: [
          'Martingale+LP combined strategy 60%+ annual yield with regime detection and risk management',
          'Built AI digital twin, multi-agent research system, and DeFi information agent',
        ],
        tags: ['Quant Strategy', 'Uniswap V3', 'AI Agent', 'RAG'],
      },
      {
        year: '2022',
        title: 'Web3 Research Analyst & Product Expert',
        company: 'Chengdu Duyu Tech · AKLabs',
        desc: 'Built a systematic Web3 research and evaluation framework, distilling knowledge into publishable research and structured training.',
        bullets: [
          'Led AKLabs research column with 30+ high-quality articles published',
          'Designed and taught 30+ hours of full-stack Web3 training curriculum',
        ],
        tags: ['Research', 'Content', 'Training', 'Airdrop Strategy'],
      },
      {
        year: '2020',
        title: 'DeFi Product Manager',
        company: 'Chengdu Youyou IT',
        desc: 'Led DeFi fund product from zero to one — productizing an actively managed on-chain strategy end-to-end.',
        bullets: ['KayaLand peak TVL $2M+, 50% annual yield', 'Solo-built UniOcean Uniswap V3 position management tool'],
        tags: ['DeFi Product', 'Uniswap V3', 'TVL $2M+', 'TypeScript'],
      },
      {
        year: '2018',
        title: 'Product Manager · Web3',
        company: 'Chengdu Youyou IT',
        desc: "Designed and launched PTOHome real estate tokenization platform — one of China's earliest RWA implementations.",
        bullets: ['ERC20-based fractional property ownership and yield distribution scheme', 'Project sales $1M+'],
        tags: ['RWA', 'ERC20', 'Product Design'],
      },
    ],
  },
  projects: {
    title: 'Projects',
    subtitle: 'From DeFi protocols to AI systems',
    all: 'All',
    defi: 'DeFi',
    ai: 'AI',
    quant: 'Quant',
    research: 'Research',
    view: 'View',
    items: [
      {
        id: 'kayaland',
        title: 'KayaLand',
        subtitle: 'Decentralized Fund Platform',
        desc: 'Actively managed DeFi fund based on Uniswap V3 concentrated liquidity. Peak TVL $2M+, 50% annual yield. Led full product design and smart contract iteration.',
        tags: ['DeFi', 'Uniswap V3', 'TVL $2M+'],
        category: 'defi',
        highlight: 'TVL $2M+',
        link: null,
      },
      {
        id: 'hl-research',
        title: 'Hyperliquid HFT Research',
        subtitle: 'Market-making Infrastructure Report',
        desc: 'Commissioned by a cloud provider to conduct comprehensive research on Hyperliquid infrastructure for HFT clients. Deployed Non-Validating nodes, studied HyperBFT mechanics, latency characteristics and market-maker priority. Delivered complete HFT practice guide.',
        tags: ['HFT', 'Hyperliquid', 'Node Deploy', 'Latency'],
        category: 'research',
        highlight: '2026 Latest',
        link: '/research/hyperliquid-latency-deep-dive_en.html',
      },
      {
        id: 'ai-twin',
        title: 'AI Digital Twin',
        subtitle: 'Conversational Portfolio Platform',
        desc: 'RAG-based intelligent chat system that turns personal experience and thinking into real-time interactive AI. The website you are viewing right now.',
        tags: ['AI', 'RAG', 'LLM API'],
        category: 'ai',
        highlight: 'This Site',
        link: 'https://ai.stefans.me',
      },
      {
        id: 'multi-agent',
        title: 'Multi-Agent Research System',
        subtitle: 'Simulating a Wall Street Analyst Team',
        desc: 'LLM-driven multi-agent system simulating collaboration between different analyst roles, automatically generating structured crypto market research reports.',
        tags: ['AI', 'Multi-Agent', 'LLM', 'Research'],
        category: 'ai',
        highlight: 'AI-Native',
        link: null,
      },
      {
        id: 'uniocean',
        title: 'UniOcean',
        subtitle: 'Uniswap V3 Management Tool',
        desc: 'Independently designed and developed Uniswap V3 position management tool with deep data analytics, range visualization and position management not available in the official interface.',
        tags: ['DeFi', 'Uniswap V3', 'TypeScript', 'Solo Dev'],
        category: 'defi',
        highlight: 'Solo Built',
        link: null,
      },
      {
        id: 'nexus-quant',
        title: 'Nexus-Quant Framework',
        subtitle: 'Multi-strategy Quant Trading System',
        desc: 'In-house quant framework with market regime detection, swing trading (MSB), and alpha factor mining. Martingale+LP combined strategy achieves 60%+ annual yield.',
        tags: ['Quant', 'Python', 'Backtesting', 'Regime Detection'],
        category: 'quant',
        highlight: '60%+ Annual',
        link: null,
      },
      {
        id: 'defi-agent',
        title: 'DeFi Information Agent',
        subtitle: 'Web3 News Automation',
        desc: 'Automatically filters valuable information from massive Web3 news streams, saving 80% reading time, filtering 90%+ noise, with natural language deep-dive support.',
        tags: ['AI', 'DeFi', 'Agent', 'Automation'],
        category: 'ai',
        highlight: '80% Time Saved',
        link: null,
      },
      {
        id: 'ptohome',
        title: 'PTOHome',
        subtitle: 'Real Estate Tokenization (RWA)',
        desc: "ERC20-based property fractional ownership scheme enabling compliant real estate investment and yield distribution. Project sales $1M+. One of China's earliest RWA practices (2018).",
        tags: ['RWA', 'ERC20', 'Product Design'],
        category: 'defi',
        highlight: 'Sales $1M+',
        link: null,
      },
    ],
  },
  research: {
    title: 'Research & Portfolio',
    subtitle: 'Publicly published research',
    view_all: 'View All',
    items: [
      {
        title: 'Hyperliquid HFT Infrastructure Research',
        type: 'Technical Report',
        date: 'March 2026',
        desc: 'Hyperliquid Non-Validating node deployment, HyperBFT consensus mechanics, latency quantification and market-making strategy feasibility. Complete practice guide for HFT teams.',
        tags: ['HFT', 'Hyperliquid', 'Node Deploy'],
        link: '/research/hyperliquid-latency-deep-dive_en.html',
        featured: true,
      },
      {
        title: 'How to Evaluate Good Tokenomics (Trilogy)',
        type: 'Research Series',
        date: 'February 2023',
        desc: 'Systematic analysis of tokenomics evaluation frameworks, from conceptual definitions to quantitative analysis to real-world applications, covering the complete dimensions of token economics.',
        tags: ['Tokenomics', 'DeFi', 'Research'],
        link: 'https://paragraph.com/@aklabs-2',
        featured: true,
      },
      {
        title: 'ZK/OP/Modular Layer2 Technical Analysis',
        type: 'Technical Analysis',
        date: 'February 2023',
        desc: 'Deep comparison of ZK Rollup, Optimistic Rollup, and modular blockchain technical routes, analyzing security assumptions and use-case differences.',
        tags: ['Layer2', 'ZK', 'Modular'],
        link: 'https://paragraph.com/@aklabs-2',
        featured: false,
      },
      {
        title: 'Aevo: Decentralized Perpetuals Track',
        type: 'Project Research',
        date: 'December 2023',
        desc: 'Deep analysis of Aevo protocol architecture, order book model, liquidity mechanisms and competitive landscape.',
        tags: ['Perpetuals', 'DeFi', 'Due Diligence'],
        link: 'https://paragraph.com/@aklabs-2',
        featured: false,
      },
      {
        title: 'Veda Protocol: BTC Smart Contracts',
        type: 'Project Research',
        date: 'December 2023',
        desc: 'Research on Ordinals-based Bitcoin smart contract protocol, analyzing its technical architecture and ecosystem potential.',
        tags: ['Bitcoin', 'Ordinals', 'Smart Contracts'],
        link: 'https://paragraph.com/@aklabs-2',
        featured: false,
      },
      {
        title: 'AKLabs Research Column (30+ Articles)',
        type: 'Comprehensive Research',
        date: '2022-2024',
        desc: 'Covering tokenomics, Layer2 tech, NFT/Ordinals, DeFi protocols, airdrop strategies and more across the Web3 landscape.',
        tags: ['Web3', 'Research', '30+ Articles'],
        link: 'https://paragraph.com/@aklabs-2',
        featured: false,
      },
    ],
  },
  footer: {
    tagline: 'Web3 × AI × Quant',
    desc: 'Exploring protocol boundaries, building executable strategies and products',
    contact: 'Contact',
    email_label: 'Email',
    copyright: '© 2026 Stefans.',
    links: {
      email: 'stefans823@gmail.com',
      aklabs: 'https://paragraph.com/@aklabs-2',
    },
  },
}
